import {
  PrimaryButton,
  SecondaryButton,
  Select,
  WarningBanner,
} from "@graffer-inc/console-design-kit";
import { UserEntity } from "../../../../application/domain/user/user-entity";
import React from "react";
import { BaseModal, reportUnexpectedError } from "@graffer-inc/smart-apply";
import { BatchExternalUserIdOption } from "../../../../application/domain/user/user";
import { BatchChangeAuthMethodResult } from "../../../../application/apis/tenant-management/types";
import apiClient from "../../../../application/apis/tenant-management/tenant-management-client";
import { BatchChangeAuthMethodResultTable } from "../../../molecules/batch-change-auth-method/result-table";

type Props = {
  orgId: string;
  users: Array<UserEntity>;
  isActive: boolean;
  onCloseModal: () => void;
};

type targetUser = {
  user: UserEntity;
  newExternalUserId: string;
  error: Error | null;
};

export const BatchChangeAuthMethodToSAMLModal: React.FC<Props> = ({
  orgId,
  users,
  isActive,
  onCloseModal,
}) => {
  const [processable, setProcessable] = React.useState(false);
  const [externalUserIdOption, setExternalUserIdOption] =
    React.useState<BatchExternalUserIdOption>("email");
  const [targetUsers, setTargetUsers] = React.useState<Array<targetUser>>([]);
  const [changeUnexpcetedError, setChangeUnexpcetedError] =
    React.useState<Error>();
  const [changeResult, setChangeResult] =
    React.useState<BatchChangeAuthMethodResult>();
  const [showResultModal, setShowResultModal] = React.useState(false);

  const validateExternalUserId = (
    user: UserEntity,
    newTargetUsers: Array<targetUser>,
    opt: BatchExternalUserIdOption,
  ): Error | null => {
    if (opt === "email" && user.email === "") {
      return new Error("メールアドレスが登録されていません");
    }

    for (let i = 0; i < newTargetUsers.length; i++) {
      if (opt === "email" && newTargetUsers[i].user.email === user.email) {
        return new Error(
          `メールアドレスが他のユーザー "${newTargetUsers[i].user.accountName}" と重複しています`,
        );
      }
    }

    return null;
  };

  const onProcess = async () => {
    try {
      const param = targetUsers.map((user) => ({
        userId: user.user.id,
        externalUserId: user.newExternalUserId,
      }));
      const { data } = await apiClient.batchChangeAuthMethodToSAML(
        orgId,
        param,
      );
      setChangeResult(data);
    } catch (e) {
      reportUnexpectedError(e);
      setChangeUnexpcetedError(e);
    } finally {
      setShowResultModal(true);
    }
  };

  const onClose = () => {
    setShowResultModal(false);
    onCloseModal();
  };

  React.useEffect(() => {
    const newTargetUsers: Array<targetUser> = [];
    users.forEach((user) => {
      const error = validateExternalUserId(
        user,
        newTargetUsers,
        externalUserIdOption,
      );

      newTargetUsers.push({
        user: user,
        newExternalUserId:
          externalUserIdOption === "accountName"
            ? user.accountName
            : user.email,
        error: error,
      });
    });

    setTargetUsers(newTargetUsers);
    setProcessable(newTargetUsers.every((user) => user.error === null));
  }, [externalUserIdOption, users]);

  const changeResultModalContent = () => {
    if (!changeResult) {
      return null;
    }

    return (
      <>
        <section className="modal-card-body">
          <br />
          {changeUnexpcetedError ? (
            <WarningBanner message="エラーが発生しました。時間をおいてから再度実行してください。" />
          ) : (
            BatchChangeAuthMethodResultTable({ targetUsers, changeResult })
          )}
        </section>
        <footer
          className="modal-card-foot"
          style={{ justifyContent: "center" }}
        >
          <PrimaryButton onClick={onClose} text={"閉じる"} />
        </footer>
      </>
    );
  };

  const modalContent = () => {
    return (
      <>
        <section className="modal-card-body">
          <p>以下のユーザーの認証方式をSAML認証に変更します。</p>
          <br />
          <Select
            description="外部ユーザーIDに使用する値を選択してください。メールアドレスを使用する場合、ユーザーにメールアドレスが登録されている必要があります。"
            label="外部ユーザーIDの設定"
            onChange={(v) => {
              setExternalUserIdOption(v);
            }}
            options={[
              {
                name: "アカウント名",
                value: "accountName",
              },
              {
                name: "メールアドレス",
                value: "email",
              },
            ]}
            required
            value={externalUserIdOption}
          />
          <br />
          <table className="table is-bordered is-fullwidth">
            <thead>
              <tr>
                <th>アカウント名</th>
                <th>外部ユーザーID</th>
              </tr>
            </thead>
            <tbody>
              {targetUsers.map((target) => (
                <tr key={target.user.accountName}>
                  <td>{target.user.accountName}</td>
                  <td
                    style={target.error ? { backgroundColor: "#fdfce6" } : {}}
                  >
                    {target.error
                      ? target.error.message
                      : target.newExternalUserId}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ marginTop: "1.5rem" }}>
            <WarningBanner
              message="外部ユーザーIDが正しいかご確認ください"
              description="アカウントの紐づけに誤りがあると、別のユーザーがログインできる可能性があります。確認のうえ、変更してください。"
            />
          </div>
        </section>
        <footer
          className="modal-card-foot"
          style={{ justifyContent: "center" }}
        >
          <SecondaryButton onClick={onClose} text={"キャンセル"} />
          <PrimaryButton
            disabled={!processable}
            onClick={onProcess}
            text={"変更を実行"}
          />
        </footer>
      </>
    );
  };

  return (
    <>
      <BaseModal
        title="認証方式の一括変更(SAML)"
        isActive={isActive}
        onClose={onClose}
      >
        {showResultModal ? changeResultModalContent() : modalContent()}
      </BaseModal>
    </>
  );
};
